// app/(todos)/todos/components/TodoItem.tsx
'use client';

import { type TodoTask } from '@prisma/client';
import { format, isPast, isToday } from 'date-fns';
import { ru } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { Check, Copy, Edit, Flag, Trash2 } from 'lucide-react';
import { memo, useCallback, useMemo, useOptimistic, useState } from 'react';
import toast from 'react-hot-toast';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import { deleteTask, updateTaskCompletion, updateTaskSchedule } from '../lib/actions/todoActions';
import type { WeatherHour } from '../lib/forecastClient';
import { ReminderPicker } from './ReminderPicker';
import WeatherIcon from './WeatherIcon';

const priorityMap: Record<number, { label: string; icon: string }> = {
  1: { label: 'Срочно', icon: 'text-red-500' },
  2: { label: 'Высокий', icon: 'text-orange-500' },
  3: { label: 'Средний', icon: 'text-sky-500' },
  4: { label: 'Низкий', icon: 'text-slate-500' },
};

type TodoItemProps = {
  task: TodoTask;
  weatherHour?: WeatherHour | null;

  // быстрый UX: сначала убираем из UI, потом сервер
  onDeleteOptimistic: (taskId: string) => void;

  // обновить задачу в стейте списка
  onUpdate: (updatedTask: TodoTask) => void;

  // открыть общий диалог
  onOpenDetails: (taskId: string) => void;
  onOpenEdit: (taskId: string) => void;
};

function TodoItemComponent({
  task,
  weatherHour = null,
  onDeleteOptimistic,
  onUpdate,
  onOpenDetails,
  onOpenEdit,
}: TodoItemProps) {
  const [optimisticTask, setOptimisticTask] = useOptimistic(
    task,
    (state: TodoTask, payload: Partial<TodoTask> | boolean) => {
      if (typeof payload === 'boolean') return { ...state, isCompleted: payload };
      return { ...state, ...payload };
    },
  );

  const [isSavingCompletion, setIsSavingCompletion] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const priority = priorityMap[optimisticTask.priority] ?? priorityMap[4];

  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  const saveReminderAt = useCallback(
    async (next?: Date) => {
      if (isSavingSchedule || isDeleting) return;

      const prev = optimisticTask.reminderAt ?? null;
      const nextVal = next ?? null;

      setIsSavingSchedule(true);
      setOptimisticTask({ reminderAt: nextVal });

      try {
        const updated = await updateTaskSchedule(optimisticTask.id, { reminderAt: nextVal });
        // сервер — источник истины
        onUpdate(updated as any);
      } catch {
        setOptimisticTask({ reminderAt: prev });
        toast.error('Не удалось сохранить напоминание.');
      } finally {
        setIsSavingSchedule(false);
      }
    },
    [
      isSavingSchedule,
      isDeleting,
      optimisticTask.id,
      optimisticTask.reminderAt,
      onUpdate,
      setOptimisticTask,
    ],
  );

  const isOverdue = useMemo(() => {
    if (!optimisticTask.dueDate || optimisticTask.isCompleted) return false;
    const due = new Date(optimisticTask.dueDate);
    return isPast(due) && !isToday(due);
  }, [optimisticTask.dueDate, optimisticTask.isCompleted]);

  const formattedDueDate = useMemo(() => {
    if (!optimisticTask.dueDate) return null;
    return format(new Date(optimisticTask.dueDate), 'dd MMMM', { locale: ru });
  }, [optimisticTask.dueDate]);

  const handleCheck = useCallback(async () => {
    if (isSavingCompletion || isDeleting) return;

    const prev = optimisticTask.isCompleted;
    const next = !prev;

    setIsSavingCompletion(true);
    setOptimisticTask(next);

    try {
      await updateTaskCompletion(optimisticTask.id, next);
      onUpdate({
        ...optimisticTask,
        isCompleted: next,
        completedAt: next ? new Date() : null,
      });
    } catch {
      setOptimisticTask(prev);
      toast.error('Не удалось обновить статус.');
    } finally {
      setIsSavingCompletion(false);
    }
  }, [isSavingCompletion, isDeleting, optimisticTask, onUpdate, setOptimisticTask]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(optimisticTask.content);
      toast.success('Текст задачи скопирован!');
    } catch {
      toast.error('Не удалось скопировать текст');
    }
  }, [optimisticTask.content]);

  const handleDelete = useCallback(async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    onDeleteOptimistic(optimisticTask.id);

    try {
      await toast.promise(deleteTask(optimisticTask.id), {
        loading: 'Удаление...',
        success: 'Задача удалена!',
        error: 'Ошибка удаления.',
      });
    } catch {
      toast.error('Не удалось удалить на сервере. Обнови страницу.');
    } finally {
      setIsDeleting(false);
    }
  }, [isDeleting, onDeleteOptimistic, optimisticTask.id]);

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-xl border border-slate-800 bg-slate-800/40 transition-colors hover:bg-slate-700/50',
        optimisticTask.isCompleted && 'opacity-50 bg-slate-800/20',
        isOverdue && 'border-red-500/50',
      )}
      role="listitem"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleCheck();
            }}
            aria-pressed={optimisticTask.isCompleted}
            aria-label={
              optimisticTask.isCompleted ? 'Отметить как невыполненную' : 'Отметить как выполненную'
            }
            disabled={isSavingCompletion || isDeleting}
            className={cn(
              'w-6 h-6 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-all duration-200',
              optimisticTask.isCompleted
                ? 'bg-emerald-500 border-emerald-500'
                : 'border-slate-600 hover:border-slate-400',
              (isSavingCompletion || isDeleting) && 'opacity-50 cursor-not-allowed',
            )}
          >
            <motion.div
              animate={
                optimisticTask.isCompleted ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }
              }
              transition={{ duration: 0.15 }}
            >
              <Check size={16} className="text-white" strokeWidth={3} />
            </motion.div>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          {optimisticTask.isCompleted ? 'Вернуть в работу' : 'Выполнить'}
        </TooltipContent>
      </Tooltip>

      {/* ✅ клик по задаче — открывает общий диалог */}
      <button
        type="button"
        className="flex-1 min-w-0 text-left"
        onClick={() => onOpenDetails(optimisticTask.id)}
      >
        <p
          className={cn(
            'text-base text-slate-200 truncate',
            optimisticTask.isCompleted && 'line-through text-slate-500',
          )}
        >
          {optimisticTask.content}
        </p>

        {optimisticTask.description && (
          <p
            className={cn(
              'text-sm text-slate-400 mt-0.5 line-clamp-1',
              optimisticTask.isCompleted && 'line-through',
            )}
          >
            {optimisticTask.description}
          </p>
        )}

        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {formattedDueDate && (
            <span
              className={cn('text-xs', isOverdue ? 'text-red-400 font-semibold' : 'text-slate-400')}
            >
              {formattedDueDate}
            </span>
          )}

          {/* ✅ Чип напоминания рядом с датой (редактируется прямо из списка) */}
          <ReminderPicker
            triggerVariant="chip"
            dueDate={optimisticTask.dueDate ? new Date(optimisticTask.dueDate) : undefined}
            reminderDate={
              optimisticTask.reminderAt ? new Date(optimisticTask.reminderAt) : undefined
            }
            onReminderSet={saveReminderAt}
          />

          {weatherHour && (
            <Tooltip>
              <TooltipTrigger className="flex items-center gap-1 text-xs text-slate-400">
                <WeatherIcon icon={weatherHour.condition.icon} />
                <span>{Math.round(weatherHour.temp_c)}°C</span>
              </TooltipTrigger>
              <TooltipContent>{weatherHour.condition.text}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </button>

      <div className="flex items-center gap-2 sm:gap-3 ml-auto pl-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center">
              <Flag size={18} className={priority.icon} />
            </span>
          </TooltipTrigger>
          <TooltipContent>{priority.label}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleCopy();
              }}
              className="text-slate-400 hover:text-white"
            >
              <Copy size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent>Копировать текст</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenEdit(optimisticTask.id);
              }}
              aria-label="Изменить задачу"
              className="text-slate-400 hover:text-white"
            >
              <Edit size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent>Изменить</TooltipContent>
        </Tooltip>

        <AlertDialog>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Удалить задачу"
                  className="text-slate-400 hover:text-red-500"
                  disabled={isDeleting}
                >
                  <Trash2 size={16} />
                </button>
              </AlertDialogTrigger>
            </TooltipTrigger>
            <TooltipContent>Удалить</TooltipContent>
          </Tooltip>

          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить задачу?</AlertDialogTitle>
              <AlertDialogDescription>
                Действие нельзя будет отменить. Задача «{optimisticTask.content}» будет удалена.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Отмена</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Удалить</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export const TodoItem = memo(TodoItemComponent);
TodoItem.displayName = 'TodoItem';
